/**
 * THE FRONT DESK FEED — who is waiting on a person, in the top right.
 *
 * Anthony: "prompt front desk to call back immediately in notification
 * feed. Add notification feed in top right."
 *
 * The shop had three screens that each answer a different question and none
 * of them answered "who is waiting on me". Leads is a funnel, the desk is a
 * railway, the schedule is a day. So a landline that rang and cannot be
 * texted sat in a list looking exactly like a mobile that got an opener,
 * and the one thing that needed a person inside ten minutes was one row
 * among forty.
 *
 * ─── WHAT THE BELL SAYS WITHOUT BEING OPENED ───────────────────────────
 *
 * The count is RED when anything red is open and amber when only orange is,
 * because the two mean different things to whoever is standing there. Red
 * is nobody has spoken to this person and the machine cannot. Orange is one
 * of ours waiting on an answer. A single count in one color would flatten
 * that back into "there are some notifications", which is what the leads
 * list already was.
 *
 * ─── CLEARING IS A CLAIM, SO IT SAYS WHO ───────────────────────────────
 *
 * Pressing Done writes who did it and when. Red is cleared automatically
 * the moment somebody actually rings the number, so the button is for the
 * case where a person dealt with it another way — and a shop where two
 * people are at the desk needs to know which of them.
 */

import { db } from "../data/client.ts";
import { startLive } from "../data/live.ts";
import { esc, ago, since } from "./dom.ts";

export type Note = {
  id: string;
  level: "red" | "orange";
  kind: string;
  title: string;
  detail: string | null;
  href: string | null;
  phone10: string | null;
  created_at: string;
  /**
   * When the thing this is about happened, which is not when the row was
   * written. Anthony: "there are alerts that say someone didn't answer 8
   * minutes ago, for ten minutes." Two clocks, neither of them the one
   * that matters. This is the one that matters.
   */
  since_at: string | null;
};

/** What the card counts from. Falls back only for rows written before this existed. */
export const clockOf = (n: Note): string => n.since_at ?? n.created_at;

let open: Note[] = [];
let feedLive: (() => void) | undefined;

export async function loadFeed(): Promise<Note[]> {
  const sb = await db();
  const { data, error } = await sb.from("notifications")
    .select("id,level,kind,title,detail,href,phone10,created_at,since_at")
    .is("cleared_at", null)
    // Red above orange, then newest first, which is the rule everywhere.
    .order("level")            // 'orange' < 'red' alphabetically, so reversed below
    .order("created_at", { ascending: false })
    .limit(60);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Note[];
  return [...rows].sort((a, b) =>
    (a.level === b.level ? 0 : a.level === "red" ? -1 : 1)
    || b.created_at.localeCompare(a.created_at));
}

/**
 * THE PANEL OPENS WHEN IT IS ASKED TO, AND NOT BEFORE.
 *
 * Anthony: "for notifications the feed should not load out unless clicked,
 * that's annoying af."
 *
 * I put a red strip under the header this morning off an earlier note —
 * "alerts only pop out when you click bell, anything that's an alert
 * should have updated in the leads and other cards too". I read that as
 * asking for them to be visible without a click. It was asking for the
 * alerts to REACH THE CARDS, which is a different thing and is done: a
 * flagged lead is red or orange on its own tile, from the same rows the
 * bell counts.
 *
 * So the strip is gone. The bell still carries the color and the count,
 * which is what tells somebody there is something to open without
 * anything covering the screen they are working on.
 */

/** The bell, its count, and the panel under it. */
export function mountFeed(hostId: string): void {
  const host = document.getElementById(hostId);
  if (!host) return;

  const paint = (): void => {
    const reds = open.filter((n) => n.level === "red").length;
    const total = open.length;
    host.innerHTML = `
      <button id="feedbell" class="feedbell${reds ? " red" : total ? " amber" : ""}"
        aria-haspopup="true" aria-expanded="false"
        title="${total ? `${total} waiting on somebody` : "Nothing waiting"}">
        ${bell()}${total ? `<i class="feedn">${total}</i>` : ""}
      </button>
      <div id="feedpanel" class="feedpanel" hidden>
        <div class="feedpanel-head">Waiting on somebody
          ${reds ? `<b class="bad">${reds} urgent</b>` : ""}</div>
        ${total ? open.map(rowOf).join("")
          : `<div class="feedempty">Nobody is waiting. Everything that came in
               has been answered.</div>`}
      </div>`;

    const bellBtn = document.getElementById("feedbell");
    const panel = document.getElementById("feedpanel");
    bellBtn?.addEventListener("click", (e) => {
      e.stopPropagation();
      if (!panel) return;
      panel.hidden = !panel.hidden;
      bellBtn.setAttribute("aria-expanded", String(!panel.hidden));
    });

    panel?.querySelectorAll<HTMLButtonElement>("[data-done]").forEach((b) =>
      b.addEventListener("click", async (e) => {
        e.stopPropagation();
        e.preventDefault();
        b.disabled = true;
        try {
          await clearOne(b.dataset["done"] as string);
          open = open.filter((n) => n.id !== b.dataset["done"]);
          paint();
          const p = document.getElementById("feedpanel");
          if (p) p.hidden = false;                    // stay where they were
        } catch { b.disabled = false; }
      }));

    // A tap anywhere else closes it. A panel that needs its own button to
    // shut is a panel people leave open over the screen.
    document.addEventListener("click", () => {
      const p = document.getElementById("feedpanel");
      if (p && !p.hidden) p.hidden = true;
    }, { once: true });
  };

  const rowOf = (n: Note) => `
    <a class="feedrow ${n.level}" href="${esc(n.href ?? "#/leads")}">
      <span class="feeddot"></span>
      <span class="feedmain">
        <span class="feedtitle">${
          n.kind === "landline_caller" ? `<b class="lead-landline">LANDLINE</b> ` : ""
        }${esc(n.title)}</span>
        ${n.detail ? `<span class="feeddetail">${esc(n.detail)}</span>` : ""}
        <!-- A MENTION IS NOT A LEAD GOING COLD. Every other row here is
             something nobody has answered, so the clock reads "waiting".
             A colleague tagging you is not waiting on the shop, it is a
             thing that happened, and it says so. -->
        <span class="feedwhen">${
          n.kind === "mention"
            ? esc(since(clockOf(n)))
            : `${esc(ago(clockOf(n)))}${
                n.level === "red" ? " with no answer" : " waiting"}`}${
          n.phone10 && n.kind !== "mention" ? ` &middot; ${esc(dial(n.phone10))}` : ""}</span>
      </span>
      <button class="feeddone" data-done="${esc(n.id)}" title="Somebody dealt with this"
        aria-label="Done">&#10003;</button>
    </a>`;

  const refresh = async (): Promise<void> => {
    try {
      open = await loadFeed();
      /**
       * IT OPENED ITSELF ON EVERY PAGE CHANGE, and this line is why.
       *
       * Anthony: "It keeps popping every time you change a page and is
       * super annoying af."
       *
       * `?.hidden` on an element that does not exist yet is `undefined`,
       * and `!undefined` is TRUE. Navigating rebuilds the top bar, so the
       * panel is gone at the moment this runs, so "was it open" answered
       * yes every single time and the next line dutifully opened it.
       *
       * Asking whether it is EXPLICITLY not hidden has only one answer
       * when there is nothing to ask about.
       */
      const wasOpen = document.getElementById("feedpanel")?.hidden === false;
      paint();
      const p = document.getElementById("feedpanel");
      if (p && wasOpen) p.hidden = false;
    } catch {
      // A bell that cannot count is not worth an error over the shop's
      // screen. It simply shows nothing until the next tick.
    }
  };

  void refresh();
  /**
   * SUBSCRIBED ONCE, KEPT FOR THE SESSION. The top bar is rebuilt on every
   * route (that is why mountFeed runs again and again), but the socket must
   * NOT be torn down and re-opened with it — so the subscription is created
   * on the first mount and left alone thereafter. A new notification arrives
   * by subscription and repaints the bell; refresh() looks its elements up by
   * id at call time, so it keeps painting the bar that was just rebuilt around
   * it. A 60s sweep stays behind the socket for a dropped connection — the
   * same cadence the timer used to run at, now only as a backstop.
   */
  if (!feedLive) {
    feedLive = startLive({
      channel: "feed",
      table: "notifications",
      refresh,
      alive: () => true,
      fastMs: 60_000,
    });
  }
}

async function clearOne(id: string): Promise<void> {
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { error } = await sb.from("notifications").update({
    cleared_at: new Date().toISOString(),
    cleared_by: me.user?.id ?? null,
    cleared_how: "marked done at the desk",
  }).eq("id", id);
  if (error) throw new Error(error.message);
}

const dial = (p: string) => `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}`;

const bell = () =>
  `<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
     stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
     <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
     <path d="M13.7 21a2 2 0 0 1-3.4 0"></path></svg>`;
